<template>
  <div class="action_rs_box">
    <!-- 1. 映射关系看板 (Source -> Target) -->
    <div class="rs_mapping_board">
      <div class="board_inner">
        <!-- 当前按键 (只读) -->
        <div class="key_slot source">
          <div class="slot_label">{{ $t('current_selected_key') }}</div>
          <div class="key_display active">{{ currentKey }}</div>
        </div>

        <!-- 连动装饰 -->
        <div class="mapping_flow">
          <div class="flow_icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M13 17l5-5-5-5M6 17l5-5-5-5" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
          <div class="flow_label">RS</div>
        </div>

        <!-- 目标按键 (可点击更改) -->
        <trigger_btn class="key_slot target" :label="$t('mapping_action')" :vks="Keys[0]?.KeyCode || []" :tab="tab" :show-edit="true" :large="true" @trigger-click="showKeyDialog = true" />
      </div>
    </div>

    <!-- 2. 设置卡片 -->
    <div class="rs_settings_section">
      <div class="mgx_setting_item_card">
        <div class="setting_header">
          <div class="header_left">
            <svg class="pill_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" />
              <path d="M12 6v6l4 2" stroke-width="2" stroke-linecap="round" />
            </svg>
            <span class="pill_title">{{ $t('action_set_delay_time') }}</span>
          </div>
          <span class="pill_value">{{ pressTime }}<span>ms</span></span>
        </div>
        <div class="setting_slider_container">
          <input v-model.number="pressTime" class="slider_input" type="range" max="50" min="0" step="1" :style="{ backgroundSize: timeBackgroundSize }" />
          <div class="slider_labels" v-if="specialCheckCurrent(speciaBehiver.SupportEndDelayTime)">
            <span>0ms</span>
            <span>50ms</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 按键选择弹窗 -->
    <action_key_dialog v-model:visible="showKeyDialog" :row-index="0" :base-tab="tab" :selected-keys="Keys[0]?.KeyCode || [0, 0, 0]" @confirm="onKeyConfirm" />
  </div>
</template>

<script setup lang="ts">
import { VK, kks } from '@/define/define'
import { speciaBehiver, specialCheckCurrent } from '@/define/limit'
import { actionSet } from '@/service/driver/action_set'
import { SetType, BaseTabs } from '@/service/driver/action_types'
import { MagnetSeniorType } from '@libs/types/enum'
import { computed, onMounted, ref, watch, toRefs } from 'vue'
import trigger_btn from './trigger_btn.vue'
import action_key_dialog from './action_key_dialog.vue'
import { normalizeKeyCode } from '@/tool/utility'
import { operate } from '@/service/driver/operate'
import { currentSelectedKeyText, getVkText } from './action_config'

const props = defineProps<{ tab: BaseTabs; keys: VK[]; type: SetType }>()
const emit = defineEmits(['changeSelectedKeys'])
const pressTime = ref(0)
const showKeyDialog = ref(false)
let { Keys, KeyType, DelayTime } = toRefs(actionSet.magnetValue)

const currentKey = computed(() => currentSelectedKeyText.value)

const selectedKey = computed(() => {
  const code = Keys.value[0]?.KeyCode?.[2] || 0
  return getVkText(code)
})

const hasTargetKey = computed(() => {
  return Keys.value[0] && Keys.value[0].KeyCode.filter((e) => e > 0).length > 0
})

const initData = () => {
  if (Keys.value.length != 1) {
    Keys.value = [
      {
        KeyCode: [0, 0, 0],
        KeyFunction: [],
        Travel: 0,
      },
    ]
  }
  KeyType.value = MagnetSeniorType.SeniorKeyTypeRS
  pressTime.value = actionSet.modeTimes[SetType.rs]
}

onMounted(() => {
  initData()
})

watch(
  () => JSON.stringify(actionSet.magnetValue),
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      initData()
    }
  },
  { deep: true, immediate: true },
)

watch(
  () => operate.currentKey.value,
  () => {
    initData()
  },
)

const onKeyConfirm = () => {
  const keys = normalizeKeyCode(actionSet.baseKeys.selectedKeys, actionSet.baseKeys.baseTab)
  if (Keys.value[0]) {
    Keys.value[0].KeyCode = keys
  }
}

watch(
  () => pressTime.value,
  (value) => {
    const v = Number(value)
    actionSet.modeTimes[SetType.rs] = v
    if (DelayTime.value !== v) {
      DelayTime.value = v
    }
  },
)

const timeBackgroundSize = computed(() => {
  const timePercent = (pressTime.value / 50) * 100
  return `${timePercent}%, 100%`
})
</script>

<style lang="less" scoped>
.action_rs_box {
  margin: auto;
  width: 100%;
  max-width: 620px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;

  /* 映射关系看板 - 呼吸感毛玻璃设计 */
  .rs_mapping_board {
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(20px);
    border-radius: var(--radius-m);
    padding: 20px 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.8);
    position: relative;
    /* overflow: hidden; */

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
      border-radius: var(--radius-m); /* 对齐圆角防止溢出 */
    }

    .board_inner {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 24px;
      position: relative;
      z-index: 1;
    }

    .key_slot {
      width: 130px;
      height: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 12px 14px;
      border-radius: var(--radius-l);
      background: rgba(255, 255, 255, 0.5);
      border: 1px solid rgba(0, 0, 0, 0.03);
      transition: var(--transition-base);

      .slot_label {
        font-size: 12px;
        color: var(--color-text-hint);
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 2px;
      }

      .key_display {
        font-size: 22px;
        font-weight: 900;
        color: var(--color-dark);
        line-height: 1.2;
        display: flex;
        align-items: center;
        justify-content: center;

        &.active {
          color: var(--brand-color-pure);
        }
      }

      &.target {
        cursor: pointer;
        position: relative;
        background: #fff;
        border: 1px solid rgba(0, 0, 0, 0.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
        padding: 0;
        height: 80px;

        &:hover {
          border-color: var(--brand-color-pure);
          box-shadow: var(--action-btn-shadow-hover);
          transform: translateY(-3px);
        }

        &.empty {
          .key_display {
            font-size: 14px;
            color: var(--color-text-hint);
            font-weight: 600;
          }
        }

        .edit_hint {
          position: absolute;
          top: 10px;
          right: 10px;
          width: 14px;
          height: 14px;
          color: var(--color-text-hint);
          opacity: 0.4;
        }
      }
    }

    .mapping_flow {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;

      .flow_icon {
        width: 24px;
        height: 24px;
        color: var(--brand-color-pure);
        filter: drop-shadow(0 0 8px rgba(255, 124, 0, 0.3));
      }
      .flow_label {
        font-size: 9px;
        font-weight: 900;
        color: var(--brand-color-pure);
        letter-spacing: 2px;
        opacity: 0.7;
      }
    }
  }

  /* 设置区块 */
  .rs_settings_section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
